import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { getProjects, adkChat, getAdkStatus } from '../services/api';
import { Button } from '../components/ui/button';
import { Sparkles, Send, Bot, User, Loader2, Wrench, FlaskConical } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

const genSession = (projectId) => `${projectId || 'none'}-${Math.random().toString(36).slice(2, 12)}`;

const AdkAgentPage = () => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    getProjects()
      .then((res) => setProjects(res.data || []))
      .catch(() => toast.error('Failed to load projects'));
    getAdkStatus()
      .then((res) => setStatus(res.data))
      .catch(() => setStatus({ enabled: false }));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleProjectChange = (e) => {
    const id = e.target.value;
    setProjectId(id);
    setSessionId(genSession(id));
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const sid = sessionId || genSession(projectId || 'all');
    if (!sessionId) setSessionId(sid);

    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await adkChat({ project_id: projectId || null, message: text, session_id: sid });
      const data = res.data || {};
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: data.answer || '(no answer)', tools: data.tools_used || [] },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'The agent failed to respond.';
      setMessages((m) => [...m, { role: 'assistant', text: `⚠️ ${detail}`, tools: [] }]);
      toast.error('ADK agent error');
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-col" style={{ height: 'calc(100vh - 0px)' }}>
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#2B8FD8] to-indigo-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                AI Agent
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  <FlaskConical className="h-3 w-3" /> Labs
                </span>
              </h1>
              <p className="text-sm text-slate-500">
                Tool-using agent (Google ADK + {status?.model || 'Gemini'}) that searches docs and reads source code to answer.
              </p>
            </div>
          </div>
        </div>

        {/* Project selector */}
        <div className="mb-3 flex items-center gap-3">
          <label className="text-sm font-medium text-slate-600">Project</label>
          <select
            value={projectId}
            onChange={handleProjectChange}
            data-testid="adk-project-select"
            className="flex-1 max-w-md text-sm border border-slate-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#2B8FD8]"
          >
            <option value="">🌐 All projects (org-wide consultant)</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.customer_name ? ` — ${p.customer_name}` : ''}
              </option>
            ))}
          </select>
        </div>

        {status && status.enabled === false && (
          <div className="mb-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
            The ADK agent is currently disabled on the server (ENABLE_ADK_CHAT=false).
          </div>
        )}

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 space-y-4"
        >
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
              <Bot className="h-10 w-10 mb-2" />
              <p className="text-sm">Pick a project and ask something like<br />
                <span className="text-slate-600">“Why might the invoice sync fail?”</span> or
                <span className="text-slate-600"> “How is validation implemented?”</span>
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-[#2B8FD8] flex items-center justify-center shrink-0">
                  <Bot className="h-4 w-4 text-white" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                m.role === 'user'
                  ? 'bg-[#2B8FD8] text-white rounded-br-sm'
                  : 'bg-slate-50 border border-slate-200 text-slate-800 rounded-bl-sm'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none prose-pre:bg-slate-900 prose-pre:text-slate-100">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                  </div>
                ) : (
                  <span className="whitespace-pre-wrap">{m.text}</span>
                )}
                {m.tools && m.tools.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[...new Set(m.tools)].map((t, ti) => (
                      <span key={ti} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100">
                        <Wrench className="h-2.5 w-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {m.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-[#2B8FD8] flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Thinking & using tools…
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="mt-3 flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="adk-input"
            rows={1}
            placeholder="Ask the agent about this project…"
            className="flex-1 resize-none text-sm border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2B8FD8] max-h-32"
          />
          <Button onClick={send} disabled={loading || !input.trim()} data-testid="adk-send" className="bg-[#2B8FD8] hover:bg-[#2477b5] h-11">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default AdkAgentPage;
